/**
 * Play HUD race status — one leaderboard for solo (a single row) and hot-seat.
 * Header (Round / whose turn / timed Hull / Gate chips), an aligned
 * table (dot # Name Rounds Gates Time; Lap when the map has more than one)
 * with per-row finish / hull / gate-miss
 * markers, a fixed-width gate ribbon (last chevron is checkered), and finish /
 * replay footers. Time is the leader's clock; other finishers show the gap as
 * soon as they cross the goal.
 * During placement the same plate is already up: whose turn, names, and empty gates.
 * Round, lap, and time stay hidden until the race starts.
 * See docs/PLAY-HUD.md. Not the debug panel.
 */
import { CircleAlert, Lock, TriangleAlert } from 'lucide-preact';
import type { JSX } from 'preact';
import { competitorPossessive } from './competitorNames.ts';
import { FinishPopupPanel } from './finishPopup.tsx';
import { ReplayControlsPanel } from './replayControls.tsx';
import { formatElapsed, formatRaceGap } from '../../sim/highScores/index.ts';
import { overlayStore, setPanel } from '../panels.ts';
import { useStore } from '../store.ts';
import './raceHud.css';

export type RaceHudStage = 'placing' | 'aiming' | 'racing' | 'finished' | 'replay';

export type RaceHudSeat = {
  id: number;
  label: string;
  /** `#rrggbb` hull tint for the row color dot. */
  color: string;
  /** Standing place (1 = leader). Independent of the seat name. */
  place: number;
  /** 1-based round this boat is on (completed turns + 1, or the in-flight turn). */
  round: number;
  /** 1-based lap this boat is on. */
  lap: number;
  /** Gates passed on the current lap (the total lives on the ribbon below). */
  checkpointsDone: number;
  elapsedMs: number;
  /** Official finish boat-clock; null while this seat is still racing. */
  officialTimeMs: number | null;
  /** Signed ms behind the race leader; null when there is no comparable gap. */
  behindMs: number | null;
  active: boolean;
  leader: boolean;
  /** True once this seat has crossed the goal. */
  finished: boolean;
  /** Simultaneous movement: this seat's move for the round is in, and still hidden. */
  locked?: boolean;
  /** Sticky per-seat hull flag; row marker lasts until the race ends. */
  hullDamaged?: boolean;
  /** Remaining missed-gate labels; empty hides the row marker. */
  missedGates?: readonly string[];
  /** A bot sails this seat. */
  bot?: boolean;
};

export type RaceHudState = {
  stage: RaceHudStage;
  round: number;
  /** Active-seat lap, shown in the header when `laps` is greater than 1. */
  lap: number;
  /** Authored lap count. 1 hides the lap line and the Lap column. */
  laps: number;
  /** Active-seat gate count used as the ribbon fallback (e.g. finished multi). */
  checkpointsDone: number;
  checkpointsTotal: number;
  /** True when the map has a goal gate — last ribbon segment is checkered. */
  hasGoal: boolean;
  /** Screen pixels of the start-area top centre; placement pane sits just above it. */
  anchor: { x: number; y: number } | null;
  /** Placement copy when planting more than one boat. */
  placementHint: string | null;
  /** Standings rows. One per seat once the race scene is up, including placement. */
  seats: readonly RaceHudSeat[];
  /** 1-based active player, or null when the match is over / solo. */
  activePlayer: number | null;
  /** Dealt first name for the turn line; null when the match is over / solo. */
  activePlayerName: string | null;
  /**
   * Simultaneous movement only. `lockIn` is a player committing a hidden move, `reveal` is
   * the round playing for everyone. Null is sequential movement (one boat moves per turn).
   */
  roundPhase: 'lockIn' | 'reveal' | null;
  matchFinished: boolean;
  /** Hot-seat winner first name; appended to Race finished. */
  winnerLabel: string | null;
  /** Active-seat sticky hull — drives the edge vignette only. */
  hullDamaged: boolean;
  /** Timed header Hull damaged chip. */
  headerHull: boolean;
  /** Timed header Gate missed chip. */
  headerMiss: boolean;
};

export type RaceHudActions = {
  /** Only present on a test play, which is the only race with a way back to an editor. */
  onExitToEditor: (() => void) | null;
  exitLabel?: string;
};

export type RaceHudProps = {
  state: RaceHudState;
  actions: RaceHudActions;
};

export type RaceHudOptions = {
  actions: RaceHudActions;
};

export type RaceHud = {
  sync: (state: RaceHudState) => void;
  destroy: () => void;
};

const PLACEMENT_HINT = 'Place your boat in the highlighted area.';
const PLACE_STEP_HINT = 'Click to place.';
const AIMING_HINT = 'Click to set direction.';
/** Above this many gates the chevrons collapse to thin ticks so they still fit. */
const DENSE_GATE_COUNT = 24;

const INITIAL_STATE: RaceHudState = {
  stage: 'placing',
  round: 0,
  lap: 1,
  laps: 1,
  checkpointsDone: 0,
  checkpointsTotal: 0,
  hasGoal: false,
  anchor: null,
  placementHint: null,
  seats: [],
  activePlayer: null,
  activePlayerName: null,
  roundPhase: null,
  matchFinished: false,
  winnerLabel: null,
  hullDamaged: false,
  headerHull: false,
  headerMiss: false,
};

function showPlacementHint(stage: RaceHudStage): boolean {
  return stage === 'placing' || stage === 'aiming';
}

function showStandings(stage: RaceHudStage): boolean {
  return stage === 'racing' || stage === 'finished' || stage === 'replay';
}

/** Place and aim: turn line, names, and unfilled gates. No round, lap, or time. */
function showRoster(stage: RaceHudStage): boolean {
  return stage === 'placing' || stage === 'aiming';
}

function missedGateTooltip(labels: readonly string[]): string {
  if (labels.length === 0) {
    return 'Gate missed';
  }

  if (labels.length === 1) {
    return labels[0]!;
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }

  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

function CueChip({ kind }: { kind: 'hull' | 'miss' }): JSX.Element {
  const Icon = kind === 'hull' ? TriangleAlert : CircleAlert;
  const first = kind === 'hull' ? 'Hull' : 'Gate';
  const second = kind === 'hull' ? 'Damaged' : 'Missed';
  const title = `${first} ${second.toLowerCase()}`;

  return (
    <span class="race-hud-cue" data-kind={kind} role="status" title={title}>
      <Icon size={16} strokeWidth={2.25} aria-hidden="true" />
      <span class="race-hud-cue-lines">
        <span>{first}</span>
        <span>{second}</span>
      </span>
    </span>
  );
}

function standingTime(seat: RaceHudSeat): { text: string; kind: 'clock' | 'gap' | 'empty' } {
  if (seat.leader) {
    return {
      text: formatElapsed(seat.officialTimeMs ?? seat.elapsedMs),
      kind: 'clock',
    };
  }

  const text = formatRaceGap(seat.behindMs);
  return { text, kind: text === '—' ? 'empty' : 'gap' };
}

function CheckeredFlagIcon(): JSX.Element {
  return (
    <span class="race-hud-flag" aria-hidden="true">
      <span class="race-hud-flag-pole" />
      <span class="race-hud-checker">
        <span />
        <span />
        <span />
        <span />
      </span>
    </span>
  );
}

function finishHero(winnerLabel: string | null): string {
  return winnerLabel ? `Race finished — ${winnerLabel} wins!` : 'Race finished';
}

/**
 * Sequential is one boat's turn. Simultaneous asks for a hidden move instead, so the line
 * says what the player owes the round; the reveal names no one, because everyone is moving.
 */
function turnLine(
  name: string,
  roundPhase: 'lockIn' | 'reveal' | null,
): string {
  if (roundPhase === 'lockIn') {
    return `${name} locks in`;
  }

  return `${competitorPossessive(name)} turn`;
}

function RaceHeader({
  round,
  lap,
  laps,
  activePlayer,
  activePlayerName,
  roundPhase,
  matchFinished,
  winnerLabel,
  headerHull,
  headerMiss,
  roster,
}: {
  round: number;
  lap: number;
  laps: number;
  activePlayer: number | null;
  activePlayerName: string | null;
  roundPhase: 'lockIn' | 'reveal' | null;
  matchFinished: boolean;
  winnerLabel: string | null;
  headerHull: boolean;
  headerMiss: boolean;
  roster: boolean;
}): JSX.Element {
  if (roster) {
    const player = activePlayer ?? 1;

    return (
      <header class="race-hud-header">
        <div class="race-hud-title">
          <p
            key={player}
            class="race-hud-turn-line race-hud-turn-line-pulse"
            data-active-player={player}
          >
            {competitorPossessive(activePlayerName ?? `Player ${player}`)} turn
          </p>
        </div>
      </header>
    );
  }

  const solo = activePlayer === null && !matchFinished;

  return (
    <header class="race-hud-header" data-solo={solo ? 'true' : undefined}>
      <div class="race-hud-title">
        <p class="race-hud-eyebrow">Round {round}</p>
        {laps > 1 ? (
          <p class="race-hud-lap">Lap {lap} of {laps}</p>
        ) : null}
        {matchFinished ? (
          <p class="race-hud-turn-line">{finishHero(winnerLabel)}</p>
        ) : roundPhase === 'reveal' ? (
          <p class="race-hud-turn-line" data-round-phase="reveal">
            All boats move
          </p>
        ) : activePlayer !== null ? (
          <p
            key={activePlayer}
            class="race-hud-turn-line race-hud-turn-line-pulse"
            data-active-player={activePlayer}
            data-round-phase={roundPhase ?? undefined}
          >
            {turnLine(activePlayerName ?? `Player ${activePlayer}`, roundPhase)}
          </p>
        ) : null}
      </div>
      <div class="race-hud-header-aside">
        {headerHull || headerMiss ? (
          <div class="race-hud-header-cues">
            {headerHull ? <CueChip kind="hull" /> : <></>}
            {headerMiss ? <CueChip kind="miss" /> : <></>}
          </div>
        ) : (
          <></>
        )}
      </div>
    </header>
  );
}

function RaceTable({
  seats,
  total,
  laps,
  roster,
}: {
  seats: readonly RaceHudSeat[];
  total: number;
  laps: number;
  roster: boolean;
}): JSX.Element {
  const multiLap = !roster && laps > 1;

  return (
    <div
      class="race-hud-table"
      role="table"
      aria-label="Race standings"
      data-multi-lap={multiLap ? 'true' : undefined}
      data-roster={roster ? 'true' : undefined}
    >
      <div class="race-hud-thead" role="row">
        <span class="race-hud-col race-hud-col-swatch" role="presentation" />
        <span class="race-hud-col race-hud-col-place" role="columnheader">
          #
        </span>
        <span class="race-hud-col race-hud-col-name" role="columnheader">
          Name
        </span>
        {roster ? null : (
          <span class="race-hud-col race-hud-col-round" role="columnheader">
            Rounds
          </span>
        )}
        {multiLap ? (
          <span class="race-hud-col race-hud-col-lap" role="columnheader">
            Lap
          </span>
        ) : null}
        <span class="race-hud-col race-hud-col-gates" role="columnheader">
          Gates
        </span>
        {roster ? null : (
          <span class="race-hud-col race-hud-col-time" role="columnheader">
            Time
          </span>
        )}
      </div>
      {seats.map((seat) => {
        const time = standingTime(seat);
        const player = seat.id + 1;

        return (
          <div
            key={seat.id}
            class="race-hud-seat"
            role="row"
            data-active={seat.active ? 'true' : undefined}
            data-leader={seat.leader ? 'true' : undefined}
            data-finished={seat.finished ? 'true' : undefined}
            data-locked={seat.locked ? 'true' : undefined}
            data-place={seat.place}
            data-player={player}
          >
            <span
              class="race-hud-seat-swatch"
              role="cell"
              aria-label={`${seat.label} color`}
            >
              <span
                class="race-hud-seat-dot"
                style={{ background: seat.color }}
                data-color={seat.color}
              />
            </span>
            <span
              class="race-hud-seat-place"
              role="cell"
              aria-label={`Place ${seat.place}`}
            >
              {seat.place}
            </span>
            <span class="race-hud-seat-name" role="cell">
              <span class="race-hud-seat-name-text">{seat.label}</span>
              {seat.bot ? (
                <span class="race-hud-seat-bot" data-seat-bot title="Bot">
                  Bot
                </span>
              ) : (
                <></>
              )}
              {seat.finished ? (
                <span
                  class="race-hud-seat-flag"
                  role="img"
                  aria-label="Finished"
                  title="Finished"
                >
                  <CheckeredFlagIcon />
                </span>
              ) : (
                <></>
              )}
              {seat.locked ? (
                <span
                  class="race-hud-seat-lock"
                  role="img"
                  aria-label="Move locked in"
                  title="Move locked in"
                >
                  <Lock size={13} strokeWidth={2.5} aria-hidden="true" />
                </span>
              ) : (
                <></>
              )}
              {seat.missedGates && seat.missedGates.length > 0 ? (
                <span
                  class="race-hud-seat-miss"
                  role="img"
                  aria-label={missedGateTooltip(seat.missedGates)}
                  title={missedGateTooltip(seat.missedGates)}
                >
                  <CircleAlert size={14} strokeWidth={2.5} aria-hidden="true" />
                </span>
              ) : (
                <></>
              )}
              {seat.hullDamaged ? (
                <span
                  class="race-hud-seat-hull"
                  role="img"
                  aria-label="Hull damaged"
                  title="Hull damaged"
                >
                  <TriangleAlert size={14} strokeWidth={2.5} aria-hidden="true" />
                </span>
              ) : (
                <></>
              )}
            </span>
            {roster ? null : (
              <span
                class="race-hud-seat-round"
                role="cell"
                aria-label={`Round ${seat.round}`}
              >
                {seat.round}
              </span>
            )}
            {multiLap ? (
              <span
                class="race-hud-seat-lap"
                role="cell"
                aria-label={`Lap ${seat.lap} of ${laps}`}
              >
                {seat.lap}/{laps}
              </span>
            ) : null}
            <span
              class="race-hud-seat-gates"
              role="cell"
              aria-label={`${seat.checkpointsDone} of ${total} gates`}
            >
              {seat.checkpointsDone}/{total}
            </span>
            {roster ? null : (
              <span
                class="race-hud-seat-time"
                role="cell"
                data-time={time.kind}
                aria-label={
                  time.kind === 'clock'
                    ? time.text
                    : time.kind === 'empty'
                      ? 'No gap'
                      : `Gap ${time.text}`
                }
              >
                {time.text}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GateRibbon({
  done,
  total,
  hasGoal,
  pending,
}: {
  done: number;
  total: number;
  hasGoal: boolean;
  /** Placement: every chevron stays empty, including the next gate. */
  pending: boolean;
}): JSX.Element {
  if (total <= 0) {
    return <></>;
  }

  const cappedDone = Math.min(done, total);
  const dense = total > DENSE_GATE_COUNT;

  return (
    <div
      class="race-hud-gates"
      role="img"
      aria-label={`Gates ${cappedDone} of ${total}`}
      data-dense={dense ? 'true' : undefined}
    >
      <span class="race-hud-gates-label">Gates</span>
      <div class="race-hud-gates-track">
        {Array.from({ length: total }, (_, index) => {
          const state = pending
            ? 'todo'
            : index < cappedDone
              ? 'done'
              : index === cappedDone
                ? 'current'
                : 'todo';
          const isGoal = hasGoal && index === total - 1;

          return (
            <span
              key={index}
              class="race-hud-gate-seg"
              data-state={state}
              data-goal={isGoal ? 'true' : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

function DamageVignette({ damaged }: { damaged: boolean }): JSX.Element {
  return (
    <div
      class="race-hud-damage-vignette"
      data-damaged={damaged ? 'true' : undefined}
      hidden={!damaged}
      aria-hidden="true"
    />
  );
}

function PlacementHint({
  stage,
  placementHint,
}: {
  stage: RaceHudStage;
  placementHint: string | null;
}): JSX.Element {
  const placementCopy = placementHint ?? PLACEMENT_HINT;

  return (
    <div class="race-hud-hint">
      {stage === 'aiming' ? (
        <>
          {placementHint ? <p>{placementHint}</p> : null}
          <p>{AIMING_HINT}</p>
        </>
      ) : (
        <>
          <p>{placementCopy}</p>
          <p>{PLACE_STEP_HINT}</p>
          <p>{AIMING_HINT}</p>
        </>
      )}
    </div>
  );
}

export function RaceHudPanel({ state, actions }: RaceHudProps): JSX.Element {
  const onExit = actions.onExitToEditor;
  const hint = showPlacementHint(state.stage);
  const anchored = hint && state.anchor !== null;
  const roster = showRoster(state.stage);
  const standings = roster || showStandings(state.stage);
  const panels = useStore(overlayStore);
  const finish = panels.finishPopup;
  const replay = panels.replayControls;
  const showFinish = state.stage === 'finished' && finish !== null;
  const showReplay = state.stage === 'replay' && replay !== null;
  const showExit = onExit && state.stage !== 'finished' && state.stage !== 'replay';

  const activeSeat = state.seats.find((seat) => seat.active) ?? null;
  const ribbonDone = roster ? 0 : (activeSeat?.checkpointsDone ?? state.checkpointsDone);
  const headerLap = activeSeat?.lap ?? state.lap;
  const hintNode = hint ? (
    <PlacementHint stage={state.stage} placementHint={state.placementHint} />
  ) : null;

  return (
    <>
      <DamageVignette damaged={state.hullDamaged} />
      {anchored && state.anchor && hintNode ? (
        <div
          class="race-hud-hint-anchor"
          style={
            {
              '--race-hud-x': `${state.anchor.x}px`,
              '--race-hud-y': `${state.anchor.y}px`,
            } as JSX.CSSProperties
          }
        >
          {hintNode}
        </div>
      ) : null}
      <aside class="race-hud" aria-label="Race status" aria-live="polite">
        {anchored ? null : hintNode}
        <div class="race-hud-body" hidden={!standings}>
          <div
            class="race-hud-plate"
            data-match-finished={state.matchFinished ? 'true' : undefined}
            data-roster={roster ? 'true' : undefined}
          >
            <RaceHeader
              round={state.round}
              lap={headerLap}
              laps={state.laps}
              activePlayer={state.activePlayer}
              activePlayerName={state.activePlayerName}
              roundPhase={state.roundPhase}
              matchFinished={state.matchFinished}
              winnerLabel={state.winnerLabel}
              headerHull={state.headerHull}
              headerMiss={state.headerMiss}
              roster={roster}
            />
            <RaceTable
              seats={state.seats}
              total={state.checkpointsTotal}
              laps={state.laps}
              roster={roster}
            />
            <GateRibbon
              done={ribbonDone}
              total={state.checkpointsTotal}
              hasGoal={state.hasGoal}
              pending={roster}
            />
            {showFinish || showReplay ? (
              <div class="race-hud-footer ui-chrome">
                {showReplay && replay ? (
                  <ReplayControlsPanel {...replay} />
                ) : showFinish && finish ? (
                  <FinishPopupPanel {...finish} />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {showExit ? (
          <div class="race-hud-actions ui-chrome">
            <button type="button" class="ui-button" onClick={onExit}>
              {actions.exitLabel ?? 'Back to editor'}
            </button>
          </div>
        ) : (
          <></>
        )}
      </aside>
    </>
  );
}

export function mountRaceHud({ actions }: RaceHudOptions): RaceHud {
  setPanel('raceHud', { state: INITIAL_STATE, actions });

  return {
    sync: (state) => setPanel('raceHud', { state, actions }),
    destroy: () => setPanel('raceHud', null),
  };
}
